import { useMemo, useState } from "react";
import Dialog from "../../common/Dialog";
import {
  Tabs,
  TabsContent as BaseTabsContent,
  TabsList,
  TabsTrigger,
} from "../../shadcn/Tabs";
import Button from "../../core/Button";
import { Download, FileText, Tags, AlertTriangle, Printer } from "lucide-react";
import csvPreview from "./csv-preview.png";
import labelsPreview from "./labels-preview.png";
import pdfPreview from "./pdf-preview.png";
import { FieldWrapper } from "../../core/FieldWrapper";
import { RadioGroup, RadioGroupItem } from "../../shadcn/RadioGroup";
import { Label } from "../../shadcn/Label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../shadcn/Select";

interface WarningMessageProps {
  children: React.ReactNode;
}

const TabsContent = ({
  children,
  value,
}: {
  children: React.ReactNode;
  value: string;
}) => (
  <BaseTabsContent
    value={value}
    className="mt-4 space-y-6 text-gray-secondary min-h-[250px]"
  >
    {children}
  </BaseTabsContent>
);

const WarningMessage = ({ children }: WarningMessageProps) => (
  <div className="mt-4 flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
    <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
    <p className="text-sm text-yellow-800">{children}</p>
  </div>
);

const SHOW_DOWNLOAD_WARNING_THRESHOLD = 1000;
const SHOW_PRINT_WARNING_MULTI_PAGE_THRESHOLD = 1000;
const SHOW_PRINT_WARNING_SINGLE_PAGE_THRESHOLD = 500;

export enum OutputType {
  print = "print",
  export = "export",
}

enum PrintType {
  single = "single",
  multiple = "multiple",
}

interface ListOption {
  value: string;
  label: string;
}

interface PrintExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  numPrintRecords?: number;
  onListingsCsvExportClick: () => void;
  onListingsPdfExportClick: () => void;
  onListingsPdfPrintClick: () => void;
  onLabelsPrintClick: () => void;
  onLabelsExportClick: () => void;
  printType?: string;
  onPrintTypeChange?: (value: string) => void;
  isFetchingListings?: boolean;
  isDownloadingListings?: boolean;
  outputType?: OutputType;
  isSuppressPrintTypeSelection?: boolean;
  isSuppressAveryLabels?: boolean;
  exportTemplateOptions?: ListOption[];
  selectedExportTemplate?: string;
  onExportTemplateChange?: (value: string) => void;
}

const ButtonsBar = ({
  onCancelClick,
  onExportClick,
  outputType,
  isPrinting = false,
  isPrintDisabled = false,
  isExportDisabled = false,
  isExporting = false,
  onPrintClick,
}: {
  onCancelClick?: () => void;
  onExportClick?: () => void;
  onPrintClick?: () => void;
  outputType?: OutputType;
  isPrinting?: boolean;
  isPrintDisabled?: boolean;
  isExportDisabled?: boolean;
  isExporting?: boolean;
}) => {
  return (
    <div className="buttons-container flex justify-end gap-2 mt-6">
      <Button variant="outline" onClick={onCancelClick}>
        Cancel
      </Button>
      {outputType === OutputType.export && (
        <Button
          disabled={isExportDisabled}
          isLoading={isExporting}
          onClick={onExportClick}
          className="flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Export
        </Button>
      )}
      {outputType === OutputType.print && (
        <Button
          disabled={isPrintDisabled}
          onClick={onPrintClick}
          isLoading={isPrinting}
          className="flex items-center gap-2"
        >
          <Printer className="h-4 w-4" />
          Print
        </Button>
      )}
    </div>
  );
};

export const PrintExportDialog = ({
  open,
  onOpenChange,
  numPrintRecords = 0,
  onListingsCsvExportClick,
  onListingsPdfExportClick,
  onListingsPdfPrintClick,
  onLabelsPrintClick,
  onLabelsExportClick,
  printType = PrintType.single,
  onPrintTypeChange,
  isFetchingListings = false,
  isDownloadingListings = false,
  outputType = OutputType.print,
  isSuppressPrintTypeSelection = false,
  isSuppressAveryLabels = false,
  exportTemplateOptions = [],
  selectedExportTemplate,
  onExportTemplateChange,
}: PrintExportDialogProps) => {
  const [activeTab, setActiveTab] = useState(
    outputType === OutputType.print ? "pdf" : "csv"
  );

  const isPrintDisabled = useMemo(() => {
    if (printType === PrintType.multiple) {
      return numPrintRecords > SHOW_PRINT_WARNING_MULTI_PAGE_THRESHOLD;
    }
    return numPrintRecords > SHOW_PRINT_WARNING_SINGLE_PAGE_THRESHOLD;
  }, [printType, numPrintRecords]);

  const dialogTitle = useMemo(() => {
    const action = outputType === OutputType.print ? "Print" : "Export";
    const itemWord = numPrintRecords === 1 ? "property" : "properties";
    return `${action} ${numPrintRecords} ${itemWord}`;
  }, [outputType, numPrintRecords]);

  return (
    <Dialog
      isOpen={open}
      onOpenChange={onOpenChange}
      title={dialogTitle}
    >
      <div className="flex flex-col">
        {activeTab === "csv" &&
          outputType === OutputType.export &&
          numPrintRecords > SHOW_DOWNLOAD_WARNING_THRESHOLD && (
            <WarningMessage>
              You are currently planning to export more than&nbsp;
              {SHOW_DOWNLOAD_WARNING_THRESHOLD} records. This may take a while.
            </WarningMessage>
          )}
        {activeTab === "pdf" &&
          printType === PrintType.multiple &&
          numPrintRecords > SHOW_PRINT_WARNING_MULTI_PAGE_THRESHOLD && (
            <WarningMessage>
              You are currently planning to print more than&nbsp;
              {SHOW_PRINT_WARNING_MULTI_PAGE_THRESHOLD} records. Please refine
              your search criteria before printing.
            </WarningMessage>
          )}
        {activeTab === "pdf" &&
          printType === PrintType.single &&
          numPrintRecords > SHOW_PRINT_WARNING_SINGLE_PAGE_THRESHOLD && (
            <WarningMessage>
              You are currently planning to print more than&nbsp;
              {SHOW_PRINT_WARNING_SINGLE_PAGE_THRESHOLD} records. Please refine
              your search criteria before printing.
            </WarningMessage>
          )}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          {!isSuppressAveryLabels && (
            <TabsList className="grid grid-cols-3">
              {outputType === OutputType.export && (
                <TabsTrigger value="csv" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  CSV
                </TabsTrigger>
              )}
              {outputType === OutputType.print && (
                <TabsTrigger value="pdf" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  PDF
                </TabsTrigger>
              )}
              <TabsTrigger value="labels" className="flex items-center gap-2">
                <Tags className="h-4 w-4" />
                Labels
              </TabsTrigger>
            </TabsList>
          )}

          {/* CSV Tab */}
          {outputType === OutputType.export && (
            <TabsContent value="csv">
              <div className="content flex items-center gap-4 min-h-[150px]">
                <div className="preview w-[150px] aspect-square bg-gray-100 border border-gray-300 rounded-lg shadow-sm flex-shrink-0 relative cursor-pointer overflow-hidden">
                  <img
                    src={csvPreview}
                    alt="CSV Preview"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <span className="text-xs text-white">Click to enlarge</span>
                  </div>
                </div>
                <div className="description flex-1 flex flex-col justify-center gap-3">
                  <p className="text-sm">
                    All available data for each lead will be outputted in a
                    spreadsheet-friendly format. CSV files can be opened in MS
                    Excel and Google Sheets. It's also a great format to upload
                    into dialers or CRM's.
                  </p>
                  {exportTemplateOptions.length > 0 && (
                    <FieldWrapper fieldLabel="Export Template">
                      <Select
                        value={selectedExportTemplate}
                        onValueChange={onExportTemplateChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select export template" />
                        </SelectTrigger>
                        <SelectContent 
                          className="z-[9999]"
                          position="popper"
                          sideOffset={4}
                        >
                          {exportTemplateOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FieldWrapper>
                  )}
                </div>
              </div>
              <ButtonsBar
                onCancelClick={() => onOpenChange(false)}
                outputType={outputType}
                isExporting={isDownloadingListings}
                onExportClick={onListingsCsvExportClick}
              />
            </TabsContent>
          )}

          {/* PDF Tab */}
          {outputType === OutputType.print && (
            <TabsContent value="pdf">
              <div className="content flex items-center gap-4 min-h-[150px]">
                <div className="preview w-[150px] aspect-square bg-gray-100 border border-gray-300 rounded-lg shadow-sm flex-shrink-0 relative cursor-pointer overflow-hidden">
                  <img
                    src={pdfPreview}
                    alt="PDF Preview"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <span className="text-xs text-white">Click to enlarge</span>
                  </div>
                </div>
                <div className="description flex-1 flex flex-col justify-center gap-3">
                  <p className="text-sm">
                    Key contact phone number and address information from the
                    list layout will be exported in a print friendly PDF format.
                    Each entry will also include basic property information,
                    notes, tags, and favorite status.
                  </p>
                  {!isSuppressPrintTypeSelection && (
                    <FieldWrapper fieldLabel="Print Per Page">
                      <RadioGroup
                        value={printType}
                        onValueChange={onPrintTypeChange}
                        className="flex flex-row p-2"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value={PrintType.single}
                            id="listings-per-page-single"
                          />
                          <Label htmlFor="listings-per-page-single">
                            Single
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value={PrintType.multiple}
                            id="listings-per-page-multiple"
                          />
                          <Label htmlFor="listings-per-page-multiple">
                            Multiple
                          </Label>
                        </div>
                      </RadioGroup>
                    </FieldWrapper>
                  )}
                </div>
              </div>
              <ButtonsBar
                onCancelClick={() => onOpenChange(false)}
                outputType={outputType}
                isPrintDisabled={isPrintDisabled}
                isPrinting={isFetchingListings}
                isExporting={isFetchingListings}
                isExportDisabled={isPrintDisabled}
                onPrintClick={onListingsPdfPrintClick}
                onExportClick={onListingsPdfExportClick}
              />
            </TabsContent>
          )}

          {/* Labels Tab */}
          {!isSuppressAveryLabels && (
            <TabsContent value="labels">
            <div className="content flex items-center gap-4 min-h-[150px]">
              <div className="preview w-[150px] aspect-square bg-gray-100 border border-gray-300 rounded-lg shadow-sm flex-shrink-0 relative cursor-pointer overflow-hidden">
                <img
                  src={labelsPreview}
                  alt="Labels Preview"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                  <span className="text-xs text-white">Click to enlarge</span>
                </div>
              </div>
              <div className="description flex-1 flex items-center">
                <p className="text-sm">
                  Name and mailing address data from each lead is arranged in a
                  print friendly format for labels.
                  <div className="mt-1">
                    <b>Labels per sheet:</b> 30
                  </div>
                  <div className="mt-1">
                    <b>Label Dimensions:</b> 1” x 2 5/8”
                  </div>
                  <div className="mt-1">
                    <b>Compatible templates:</b> Avery® 5160, 5260, 5520, 5660,
                    5810, 5960, 5970, 5971, 5972, 5979, 5980, 8160, 8460, 8660,
                    8810
                  </div>
                </p>
              </div>
            </div>
            <ButtonsBar
              onCancelClick={() => onOpenChange(false)}
              outputType={outputType}
              isPrinting={isFetchingListings}
              isExporting={isFetchingListings}
              onPrintClick={onLabelsPrintClick}
              onExportClick={onLabelsExportClick}
            />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Dialog>
  );
};
